import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { DateInput } from '@/components/date-input';
import { ErrorBanner } from '@/components/form';
import { StatusChip } from '@/components/migration/file-drop';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError, api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatDate } from '@/lib/format';
import {
  runReports,
  runLabel,
  useMigration,
  useParallelRuns,
  type ParallelRunView,
} from '@/lib/migration';
import { useOrgId } from '@/lib/org';

export const Route = createFileRoute('/reports/parallel-run/')({ component: ParallelRuns });

const monthAfter = (iso: string | null): { from: string; to: string } => {
  const base = iso ? new Date(`${iso}T00:00:00Z`) : new Date();
  const y = base.getUTCFullYear();
  const m = base.getUTCMonth();
  const from = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
  const to = new Date(Date.UTC(y, m + 1, 0)).toISOString().slice(0, 10);
  return { from, to };
};

/** Screen R3 (document 06): months compared with QuickBooks during the parallel run. */
function ParallelRuns() {
  const orgId = useOrgId();
  const me = useMe();
  const runs = useParallelRuns();
  const migration = useMigration();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const first = monthAfter(migration.data?.conversionDate ?? null);
  const [from, setFrom] = useState(first.from);
  const [to, setTo] = useState(first.to);
  const create = useMutation({
    mutationFn: () =>
      api<ParallelRunView>(`/orgs/${orgId}/parallel-runs`, {
        method: 'POST',
        body: JSON.stringify({ from, to }),
      }),
    onSuccess: async (v) => {
      await qc.invalidateQueries({ queryKey: ['parallel-runs', orgId] });
      void navigate({ to: '/reports/parallel-run/$runId', params: { runId: v.id } });
    },
    onError: (e) => {
      const id = (e as ApiError).details as { id?: string } | undefined;
      if (e instanceof ApiError && e.code === 'DUPLICATE' && id?.id)
        void navigate({ to: '/reports/parallel-run/$runId', params: { runId: id.id } });
    },
  });
  const staff = Boolean(me.data?.isStaff);
  return (
    <>
      <PageHeader
        title="Parallel run comparison"
        description="Upload what QuickBooks reports for a month and see it beside Yedo Books, to the fils, with an explanation for every difference."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/reports">All reports</Link>
            </Button>
            {staff ? (
              <Button
                onClick={() => {
                  const latest = runs.data?.[0]?.to;
                  // the month after the latest one compared, else the conversion month
                  const next = monthAfter(
                    latest
                      ? new Date(Date.parse(`${latest}T00:00:00Z`) + 86_400_000)
                          .toISOString()
                          .slice(0, 10)
                      : (migration.data?.conversionDate ?? null),
                  );
                  setFrom(next.from);
                  setTo(next.to);
                  setAdding(true);
                }}
              >
                Compare a month
              </Button>
            ) : null}
          </>
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : (
        <div className="flex max-w-5xl flex-col gap-4">
          {adding ? (
            <div className="flex flex-col gap-2 rounded-lg border bg-card p-4">
              <ErrorBanner>
                {create.error && (create.error as ApiError).code !== 'DUPLICATE'
                  ? create.error.message
                  : null}
              </ErrorBanner>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-sm font-medium">
                  From
                  <DateInput value={from} onChange={setFrom} aria-label="From" className="w-40" />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium">
                  To
                  <DateInput value={to} onChange={setTo} aria-label="To" className="w-40" />
                </label>
                <Button disabled={create.isPending || !from || !to} onClick={() => create.mutate()}>
                  {create.isPending ? 'Starting…' : 'Start comparing'}
                </Button>
                <Button variant="outline" onClick={() => setAdding(false)}>
                  Cancel
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Usually one calendar month. Export from QuickBooks:{' '}
                {runReports()
                  .map((r) => r.title)
                  .join(', ')}
                .
              </p>
            </div>
          ) : null}
          {runs.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : runs.isError ? (
            <EmptyState title="Could not load the comparisons" hint={runs.error.message} />
          ) : runs.data.length === 0 ? (
            <EmptyState
              title="No month compared yet"
              hint="During the parallel run, compare each month's QuickBooks reports with Yedo Books before signing it off."
            />
          ) : (
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead className="w-[180px]">QuickBooks reports</TableHead>
                    <TableHead className="w-[260px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.data.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <Link
                          to="/reports/parallel-run/$runId"
                          params={{ runId: r.id }}
                          className="font-medium text-primary hover:underline"
                        >
                          {runLabel(r.from, r.to)}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(r.from)} – {formatDate(r.to)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.uploaded} of {runReports().length} uploaded
                      </TableCell>
                      <TableCell>
                        {r.signedOffAt ? (
                          <StatusChip tone="success">
                            Signed off {formatDate(r.signedOffAt.slice(0, 10))}
                            {r.signedOffBy ? ` by ${r.signedOffBy}` : ''}
                          </StatusChip>
                        ) : (
                          <StatusChip tone="info">Open</StatusChip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </>
  );
}
